import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import React, { useState } from 'react'
import { Formik } from 'formik'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import CompanyShowroomModalFields from './CompanyShowroomModalFields'
import { ShowRoom } from 'src/Libs/company-settings/models/showroom.model'
import { Loading } from '@e3dc-react/shell/Elements/Loading'
import { makeStyles } from 'tss-react/mui'
import { Box } from '@mui/material'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { BlankViewImageVariants, BlankView, BlankViewImageSizes } from 'src/Components/blank-view'
import { fetchCoordinates } from '@e3dc-react/shell/Libs/Maps/utils'
import { Coordinates } from '@e3dc-react/shell/Libs/Maps/types'
import { LATITUDE_PLACEHOLDER, LONGITUDE_PLACEHOLDER } from '../types'
import { useCompanyShowroomFormInitializer } from '..'

type StyleProps = {
  loading: boolean
}

const useStyles = makeStyles<StyleProps>()((_, { loading }) => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingSpinner: {
    position: 'absolute',
    zIndex: 2,
  },
  form: {
    position: 'relative',
    pointerEvents: loading ? 'none' : 'auto',
  },
}))

type Props = {
  place: number
  onClose: () => void
  onSave?: (data: ShowRoom) => void
  companyId: number
}

type ConditionalProps =
  | {
      entityAccess: 'create'
      data: null
    }
  | {
      entityAccess: 'edit'
      data: ShowRoom
    }

export type CompanyShowroomProps = Props & ConditionalProps

/**
 * This is the Showroom Modal for each entityAccess (create/edit)
 * When we click on the button for showroom create we will open this modal with empty fields
 * When we click on the button for showroom editing we will open this modal with fulfilled fields
 */
const CompanyShowroomModal: React.FC<CompanyShowroomProps> = ({ data, companyId, onClose, onSave, entityAccess }) => {
  const { t } = useCustomTranslation()
  const [loading, setLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const { classes } = useStyles({ loading })
  const authFetch = useAuthFetchWithResult()
  const modalTitle = `companyShowroom.modal.${entityAccess}.title`
  const submitButtonName = `companyShowroom.modal.${entityAccess}.submitButton`
  const { initialValues, validationSchema } = useCompanyShowroomFormInitializer(data)

  const handleFormikSubmit = async (values: ShowRoom): Promise<void> => {
    if (loading) {
      return
    }

    setLoading(true)
    let coordinates: Coordinates | undefined = undefined
    try {
      coordinates = await fetchCoordinates(
        { city: values.city, countryCode: values.countryCode, address: values.address, zip: values.postalCode },
        process.env.REACT_APP_MAPS_TOKEN ?? '',
        true
      )
    } catch (err) {
      console.log(err)
    }

    const coordinatesToUse = {
      gpsLatitude: coordinates?.latitude ?? LATITUDE_PLACEHOLDER,
      gpsLongitude: coordinates?.longitude ?? LONGITUDE_PLACEHOLDER,
    }

    if (entityAccess === 'edit') {
      try {
        const { id, ...payload } = values
        await authFetch({
          path: `users/companies/${companyId}/showrooms/${id}`,
          method: 'PUT',
          payload: { ...payload, ...coordinatesToUse },
        })
      } catch (error) {
        console.error(`Error while saving showroom: ${error}`)
        setIsError(true)
      } finally {
        setLoading(false)
      }
    } else {
      try {
        await authFetch({
          path: `users/companies/${companyId}/showrooms`,
          method: 'POST',
          payload: { ...values, ...coordinatesToUse },
        })
      } catch (error) {
        console.error(`Error while sending request to create a new showroom: ${error}`)
        setIsError(true)
      } finally {
        setLoading(false)
      }
    }

    onSave && onSave({ ...values })
    onClose()
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleFormikSubmit} validationSchema={validationSchema}>
      {({ isValid, dirty, handleSubmit }) => {
        return (
          <Dialog
            title={t(modalTitle)}
            maxWidth={'md'}
            onClose={loading ? undefined : onClose}
            actions={{
              secondary: [
                {
                  name: t('common.cancel'),
                  onClick: () => {
                    if (loading) {
                      return
                    }

                    onClose()
                  },
                  disabled: loading,
                },
              ],
              primary: {
                name: t(submitButtonName),
                disabled: !isValid || !dirty || loading,
                onClick: handleSubmit,
              },
            }}
          >
            <Box className={classes.container}>
              {isError ? (
                <BlankView
                  imageVariant={BlankViewImageVariants.Worried}
                  title={'common.error'}
                  explanation={'administration.companyManagement.showroom-editor.error-view.explanation'}
                  imageSize={BlankViewImageSizes.Small}
                />
              ) : (
                <>
                  {loading && (
                    <Box className={classes.loadingSpinner}>
                      <Loading />
                    </Box>
                  )}
                  <Box className={classes.form}>
                    <CompanyShowroomModalFields />
                  </Box>
                </>
              )}
            </Box>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default CompanyShowroomModal
